import React, { useEffect, useState } from 'react';
import { CalendarClock, CheckCircle2, Plane, UserX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';
import { activityTracker } from '@/utils/activityTracker';
import { invalidateNoScheduleCache } from '@/utils/tallyNoSchedule';
import {
  ALTA_CONSENSUADA,
  ChoiceOption,
  DECIDIO_NO_CONTINUAR,
  FOLLOW_UP_WINDOW_OPTIONS,
  MAIN_REASON_OPTIONS,
  MainReasonKey,
  NO_PUDIMOS_COMBINAR,
  NoAgendamientoFormValues,
  NoAgendamientoModalParams,
  PATIENT_EVOLUTION_OPTIONS,
  SE_VA_DE_VACACIONES,
  SUB_REASON_OPTIONS,
  SUB_REASON_VACACIONES,
  emptyNoAgendamientoFormValues,
} from './noAgendamientoFormSchema';

interface NoAgendamientoModalProps {
  params: NoAgendamientoModalParams | null;
  onClose: () => void;
}

const REASON_ICONS: Record<MainReasonKey, React.ComponentType<{ className?: string }>> = {
  alta: CheckCircle2,
  'no-continua': UserX,
  'no-coordinamos': CalendarClock,
  vacaciones: Plane,
};

function ReasonCard({
  icon: Icon,
  label,
  helper,
  selected,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  helper?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        'flex items-start gap-3 rounded-xl border p-4 text-left transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted'
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        )}
      >
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{helper}</span>
      </span>
    </button>
  );
}

function CompactChoiceList({
  options,
  value,
  onChange,
}: {
  options: ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div role="radiogroup" className="flex flex-col gap-1.5">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
              selected ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted'
            )}
          >
            <span className="font-medium text-foreground">{opt.label ?? opt.value}</span>
            {opt.helper && <span className="text-xs text-muted-foreground">{opt.helper}</span>}
          </button>
        );
      })}
    </div>
  );
}

function DropdownField({
  options,
  value,
  onChange,
  ariaLabel,
  placeholder = 'Seleccioná una opción',
}: {
  options: ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label ?? opt.value}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

const SUCCESS_AUTOCLOSE_MS = 1800;

function ModalBody({ params, onClose }: { params: NoAgendamientoModalParams; onClose: () => void }) {
  const [values, setValues] = useState<NoAgendamientoFormValues>(() => ({
    ...emptyNoAgendamientoFormValues(),
    process_id: params.processId,
    patient_email: params.patientEmail,
    patient_name: params.patientName,
    psychologist_email: params.psychologistEmail,
  }));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Métricas de Actividad: el modal se abrió (inicio del par abrir → enviar de "Informar un paciente sin agendar").
  // Es un modal nativo: la ruta /patients/no-schedule es heredada y casi nadie pasa por ahí.
  // Si el paso "no aplica" (nota D), el modal no muestra el formulario: no cuenta como inicio del par.
  useEffect(() => {
    if (params.totalSessionsGrade === 'D') return;
    activityTracker.trackAction('no_schedule_opened');
    // Solo al abrirse: los params no cambian mientras el modal está montado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!submitted) return;
    const id = setTimeout(onClose, SUCCESS_AUTOCLOSE_MS);
    return () => clearTimeout(id);
  }, [submitted, onClose]);

  const set = <K extends keyof NoAgendamientoFormValues>(key: K, value: NoAgendamientoFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Cambiar de motivo principal tiene que limpiar TODO lo que dependía del motivo anterior —
   * si no, un valor de una rama que ya no se ve (ej. el sub-motivo de "No pudimos
   * coordinar") viaja igual en el payload porque nunca se borró del estado. Bug real
   * encontrado en QA en vivo 2026-09-21: quedó "Vacaciones, viaje o mudanza" como
   * sub_reason en un envío real de "El paciente decidió no continuar".
   */
  const selectReason = (value: string) => {
    setValues((prev) => ({
      ...prev,
      reason: value,
      sub_reason: '',
      follow_up_in_days: '',
      session_topics_addressed: '',
      patient_evolution: '',
      future_recommendations: '',
    }));
  };

  /** Mismo criterio que `selectReason`: cambiar el sub-motivo invalida el follow-up que
   *  dependía del sub-motivo anterior (solo aplica cuando el sub-motivo es "Vacaciones"). */
  const selectSubReason = (value: string) => {
    setValues((prev) => ({ ...prev, sub_reason: value, follow_up_in_days: '' }));
  };

  const notApplicable = params.totalSessionsGrade === 'D';
  const blockedLowSessions =
    values.reason === ALTA_CONSENSUADA && (params.totalSessionsGrade === 'B' || params.totalSessionsGrade === 'C');

  const needsClinicalSummary = values.reason === ALTA_CONSENSUADA || values.reason === DECIDIO_NO_CONTINUAR;
  const needsSubReason = values.reason === NO_PUDIMOS_COMBINAR;
  const needsFollowUpDirect = values.reason === SE_VA_DE_VACACIONES;
  const needsFollowUpViaSubReason = needsSubReason && values.sub_reason === SUB_REASON_VACACIONES;
  const needsFollowUp = needsFollowUpDirect || needsFollowUpViaSubReason;

  const canSubmit = (() => {
    if (!values.reason || blockedLowSessions) return false;
    if (needsClinicalSummary) {
      return (
        values.session_topics_addressed.trim() !== '' &&
        !!values.patient_evolution &&
        values.future_recommendations.trim() !== ''
      );
    }
    if (needsSubReason) {
      if (!values.sub_reason) return false;
      if (needsFollowUpViaSubReason) return !!values.follow_up_in_days;
      return true;
    }
    if (needsFollowUpDirect) return !!values.follow_up_in_days;
    return false;
  })();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // `values` trae también `psychologist_email` (no lo pide el backend — lo saca de
      // @AuthPsi(), nunca del body — ver NoAgendamientoService.submit); el campo de más no
      // molesta, el schema zod del backend descarta claves desconocidas en silencio.
      await apiCoreAdapter.submitNoAgendamiento(values);
      invalidateNoScheduleCache(params.psychologistEmail);
      // Métricas de Actividad (funnel no_agendamiento): se envió con éxito.
      activityTracker.trackAction('no_schedule_submitted');
      setSubmitted(true);
    } catch {
      // beHttp ya mostró el toast de error (mensaje del backend, incluido un {error} de
      // Make relayado como 409 — ver NoAgendamientoService.submit).
    } finally {
      setSubmitting(false);
    }
  };

  if (notApplicable) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <DialogTitle>Este paso no aplica acá</DialogTitle>
        <p className="max-w-sm text-sm text-muted-foreground">
          Este proceso todavía no tuvo ninguna sesión efectiva — este aviso es solo para pacientes en los que no se
          agendó una próxima sesión después de haber tenido al menos una. No hace falta que hagas nada.
        </p>
        <Button type="button" variant="outline" onClick={onClose} className="mt-2">
          Cerrar
        </Button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="h-6 w-6" />
        </span>
        <DialogTitle>Listo, quedó registrado</DialogTitle>
        <p className="text-sm text-muted-foreground">Gracias por contarnos qué pasó.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <DialogHeader>
        <DialogTitle>
          ¿Por qué no se agendó la próxima sesión{params.patientName ? ` con ${params.patientName}` : ''}?
        </DialogTitle>
      </DialogHeader>

      <div role="radiogroup" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {MAIN_REASON_OPTIONS.map((opt) => (
          <ReasonCard
            key={opt.value}
            icon={REASON_ICONS[opt.key]}
            label={opt.label ?? opt.value}
            helper={opt.helper}
            selected={values.reason === opt.value}
            onClick={() => selectReason(opt.value)}
          />
        ))}
      </div>

      {blockedLowSessions && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">No se puede registrar un alta con menos de 10 sesiones</p>
          <p className="mt-1">
            El proceso terapéutico necesita tiempo para consolidarse. Si no concertaste una próxima sesión, elegí
            &quot;No pudimos coordinar, pero sigue en tratamiento&quot; en su lugar. Si el paciente no quiere
            continuar, elegí &quot;El paciente decidió no continuar&quot;. Para derivar, usá el formulario de
            Derivación desde la ficha del paciente.
          </p>
        </div>
      )}

      {needsClinicalSummary && !blockedLowSessions && (
        <div className="flex flex-col gap-4 rounded-lg border border-border bg-muted/30 p-4">
          <Field label="¿Qué aspectos específicos se abordaron más durante las sesiones?" htmlFor="session_topics_addressed">
            <Textarea
              id="session_topics_addressed"
              rows={3}
              value={values.session_topics_addressed}
              onChange={(e) => set('session_topics_addressed', e.target.value)}
            />
          </Field>
          <Field label="Evolución del paciente">
            <DropdownField
              ariaLabel="Evolución del paciente"
              options={PATIENT_EVOLUTION_OPTIONS}
              value={values.patient_evolution}
              onChange={(v) => set('patient_evolution', v)}
            />
          </Field>
          <Field label="Recomendaciones futuras" htmlFor="future_recommendations">
            <Textarea
              id="future_recommendations"
              rows={3}
              placeholder="Para tener en cuenta si en el futuro se retoma el proceso con vos o con otro terapeuta."
              value={values.future_recommendations}
              onChange={(e) => set('future_recommendations', e.target.value)}
            />
          </Field>
        </div>
      )}

      {needsSubReason && (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4">
          <span className="text-sm font-semibold text-foreground">Contanos un poco más</span>
          <CompactChoiceList options={SUB_REASON_OPTIONS} value={values.sub_reason} onChange={selectSubReason} />
        </div>
      )}

      {needsFollowUp && (
        <Field label="¿Cuándo creés que deberíamos volver a escribirle?">
          <DropdownField
            ariaLabel="Cuándo volver a escribirle"
            options={FOLLOW_UP_WINDOW_OPTIONS}
            value={values.follow_up_in_days}
            onChange={(v) => set('follow_up_in_days', v)}
          />
        </Field>
      )}

      <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting ? 'Enviando…' : 'Enviar'}
        </Button>
      </div>
    </div>
  );
}

export const NoAgendamientoModal: React.FC<NoAgendamientoModalProps> = ({ params, onClose }) => {
  return (
    <Dialog open={params !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[46rem] max-h-[88vh] overflow-y-auto p-6">
        {params && <ModalBody params={params} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
};

export default NoAgendamientoModal;
